import i18next from "i18next";
import ICU from "i18next-icu";
import { initReactI18next } from "react-i18next";

/**
 * A single shared i18next instance for the uploader.
 * Initialised once after the backend returns texts via `initI18n`.
 */
export const i18nInstance = i18next.createInstance();

let initialised = false;

/**
 * Initialise (or reinitialise) i18next with texts from the backend.
 * Safe to call multiple times — subsequent calls swap in new resources.
 *
 * @param language - BCP-47 language code (e.g. "cs-CZ", "sk-SK", "en-US").
 * @param keys - Flat key→ICU-string map returned by the `getTexts` callable.
 */
export async function initI18n(
  language: string,
  keys: Record<string, string>
): Promise<void> {
  const resources = {
    [language]: { translation: keys },
  };

  if (!initialised) {
    await i18nInstance
      .use(ICU)
      .use(initReactI18next)
      .init({
        lng: language,
        // No fallback language: texts are baked in prod and fetched in dev.
        fallbackLng: false,
        resources,
        interpolation: { escapeValue: false },
        // Silence missing-key warnings in production.
        saveMissing: false,
        missingKeyHandler: undefined,
      });
    initialised = true;
  } else {
    // Swap resources without a full re-init.
    for (const [lng, ns] of Object.entries(resources)) {
      i18nInstance.addResourceBundle(lng, "translation", ns.translation, true, true);
    }
    await i18nInstance.changeLanguage(language);
  }
}
